/* 모델이 고른 도구 호출을 실행 전에 정본 dsl.json 으로 대조한다. 이름, 필수 인자, 참조 이름, 식 인자.
 * 식 인자는 두 문법 입구(expr.ts)를 지나 mathcore 문법으로 바뀐 것만 넘긴다. 구간별이면 호출을 조각 수만큼 늘린다 */
import { acceptsOf, acceptsText, entryOf, kindLabel, madeKind } from './dsl';
import { readCurve } from '../expr';
import { notationIssues } from '../symbols';
import type { ToolCall } from './types';

export interface Checked {
  /** 실행할 호출(식은 mathcore 문법으로 바뀜, 구간별은 조각마다 하나) */
  calls: ToolCall[];
  /** 거른 호출과 사유 */
  rejected: { call: ToolCall; detail: string }[];
  /** 반려 없이 처리한 것의 알림: 되풀이된 같은 호출은 한 번만, 겹친 이름은 뒤 호출의 이름을 뗌 */
  notices: string[];
}

const AXIS_IDS = ['0:xAxis', '0:yAxis'];
let seq = 0;
const withArgs = (call: ToolCall, args: Record<string, unknown>, suffix = ''): ToolCall => {
  seq += 1;
  return { id: `${call.id}${suffix}-${seq}`, type: 'function', function: { name: call.function.name, arguments: JSON.stringify(args) } };
};

/** 화면 개체의 종류. 부등식과 정적분은 type 이 대수 곡선(polynomial)과 같지만 곡선이 아니라 영역이라 따로 센다(inequality). 곡선을 받는 자리에 영역이 가면 화면이 멈추거나 무효가 된다 */
export function kindOfObject(o: { type?: string; inequalityExists?: boolean }): string {
  return o.type === 'polynomial' && o.inequalityExists === true ? 'inequality' : (o.type ?? '');
}

/** 식의 뿌리가 부등호이거나 정적분이면 영역이다 */
const REGION_HEADS = ['Less', 'LessEqual', 'LessOverEqual', 'Greater', 'GreaterEqual', 'GreaterOverEqual', 'Integrate'];
const isRegion = (json: unknown): boolean => Array.isArray(json) && typeof json[0] === 'string' && REGION_HEADS.includes(json[0]);

/** 호출이 세우는 개체의 type. 변환 도구는 원본과 같은 종류의 복사본을 세우므로 첫 참조 인자의 종류를 따른다(정의의 만드는 것은 모두 point 로 적혀 있다). 모르면 빈 글자 */
function madeOf(entry: NonNullable<ReturnType<typeof entryOf>>, args: Record<string, unknown>, kindOf: ReadonlyMap<string, string>, region = false): string {
  if (region) return 'inequality';
  if ((entry.path ?? [])[0] !== 'transform') return madeKind(entry);
  const first = entry.params.find((p) => p.type === 'ref');
  const origin = first && typeof args[first.name] === 'string' ? (args[first.name] as string) : '';
  return kindOf.get(origin) ?? '';
}

/** kinds: 이미 선 개체의 이름과 id → 종류(화면의 type). 이번 묶음에서 먼저 세우는 개체의 종류는 도구 정의의 「만드는 것」에서 읽는다. 정본 인자의 accepts(받는 종류)에 없는 종류가 오면 mathcore 가 개체를 만든 뒤 터지거나 쓰레기 개체를 유효로 세우므로 실행 전에 반려한다(accepts 가 any 이면 검사하지 않는다). 좌표축 문자열 id 는 accepts 에 axis 가 있는 자리에서만 개체로 인정한다.
 * known: 이미 선 개체 이름. 호출 순서대로 이름을 더해 가며 대조한다. sliders: 이미 선 슬라이더 이름(그 이름의 기호는 값 없이도 식에 쓸 수 있다) */
/** given 이 있으면 expression 과 condition 은 그 목록(제시된 식) 안의 것만 받는다. 계약은 스키마가 아니라 여기서 집행한다 */
export function validateCalls(calls: ToolCall[], known: Iterable<string>, symbols: Record<string, number> = {}, given?: string[], sliders: Iterable<string> = [], kinds: ReadonlyMap<string, string> = new Map()): Checked {
  const names = new Set(known);
  const kindOf = new Map(kinds);
  const bound = new Set(sliders);
  const out: ToolCall[] = [];
  const rejected: Checked['rejected'] = [];
  const notices: string[] = [];
  const seen = new Set<string>();
  for (let call of calls) {
    const entry = entryOf(call.function.name);
    if (!entry) { rejected.push({ call, detail: `${call.function.name} 은 dsl.json 에 없는 도구다` }); continue; }
    let args: Record<string, unknown>;
    try { const v = JSON.parse(call.function.arguments || '{}'); args = v && typeof v === 'object' && !Array.isArray(v) ? v : {}; } catch { rejected.push({ call, detail: `${call.function.name} 의 인자가 JSON 객체가 아니다` }); continue; }
    // 같은 도구를 같은 인자로 되풀이한 호출은 같은 개체다. 한 번만 세운다(되묻기 사유가 아니다)
    const key = `${call.function.name}|${JSON.stringify(args, Object.keys(args).sort())}`;
    if (seen.has(key)) { notices.push(`${call.function.name}: 같은 호출이 되풀이되어 한 번만 세운다`); continue; }
    seen.add(key);
    const missing = entry.params.filter((p) => !p.optional && (args[p.name] === undefined || args[p.name] === null || args[p.name] === '')).map((p) => p.name);
    if (missing.length) { rejected.push({ call, detail: `${call.function.name} 의 필수 인자가 비었다: ${missing.join(', ')}` }); continue; }
    const accepts = acceptsOf(entry);
    const isKnown = (v: string, slot: string): boolean => names.has(v) || (AXIS_IDS.includes(v) && (accepts.get(slot)?.includes('axis') ?? false));
    const refsOf = (p: { name: string; type: string }): string[] => {
      const v = args[p.name];
      if (p.type === 'ref') return typeof v === 'string' ? [v] : [];
      return p.type.startsWith('ref[') && Array.isArray(v) ? v.filter((one): one is string => typeof one === 'string') : [];
    };
    const badRefs = entry.params.flatMap((p) => refsOf(p).filter((v) => !isKnown(v, p.name)));
    if (badRefs.length) { rejected.push({ call, detail: `${call.function.name} 이 가리킨 ${badRefs.map((r) => `"${r}"`).join(', ')} 은 아직 세워진 개체가 아니다. 먼저 만들거나 이름을 맞춘다` }); continue; }
    const wrongKind: string[] = [];
    for (const p of entry.params) {
      const allowed = accepts.get(p.name);
      if (!allowed) continue;
      for (const v of refsOf(p)) {
        const kind = kindOf.get(v) ?? (AXIS_IDS.includes(v) ? 'axis' : undefined);
        if (kind && !allowed.includes(kind)) wrongKind.push(`${p.name} 자리의 "${v}" 은 ${kindLabel(kind)} 개체다`);
      }
    }
    if (wrongKind.length) { rejected.push({ call, detail: `${call.function.name}: ${wrongKind.join(', ')}. ${acceptsText(entry)}. 알맞은 개체의 id 로 바꾸거나, 없으면 먼저 세운다` }); continue; }
    const exprParam = entry.params.find((p) => p.type === 'expr');
    let region = false;
    let name = typeof args.name === 'string' && args.name ? args.name : '';
    // mathcore 는 요청한 이름을 중복 검사 없이 붙인다(object-manager manager.ts addObject). 겹치면 앞 개체가 조용히 가리킴을 잃으므로 뒤 호출의 이름을 떼고 별칭으로만 세운다(되묻기 사유가 아니다). 식 도구는 조각 이름을 따로 매긴다
    if (name && !exprParam && names.has(name)) { notices.push(`${call.function.name}: 이름 "${name}" 은 앞 개체가 먼저 썼다. 이 개체는 이름 없이(별칭으로만) 세운다`); delete args.name; call = withArgs(call, args); name = ''; }
    if (exprParam) {
      // condition 은 계약의 인자(식에 붙은 조건 식)다. 정본 도구의 인자가 아니므로 식에 합친 뒤 뗀다
      const { condition, ...rest } = args;
      args = rest;
      if (given) {
        const outside = [args[exprParam.name], condition].filter((v) => typeof v === 'string' && v && !given.includes(v));
        if (outside.length) { rejected.push({ call, detail: `${call.function.name} 의 식 ${outside.map((v) => `"${String(v).slice(0, 60)}"`).join(', ')} 은 제시된 식이 아니다. 문항과 풀이에 있는 식 중에서 고른다` }); continue; }
      }
      // 엔진이 상수로 읽는 그리스 명령(\varphi 등)은 값을 줄 수 없다. 그대로 두면 상수로 조용히 바뀌므로 그 식을 반려한다
      const notation = notationIssues([args[exprParam.name], condition].filter((v) => typeof v === 'string').join(' '));
      if (notation.length) { rejected.push({ call, detail: `${call.function.name} 의 식 "${String(args[exprParam.name]).slice(0, 60)}": ${notation.join('; ')}` }); continue; }
      const r = readCurve(String(args[exprParam.name] ?? ''), typeof condition === 'string' ? condition : '', symbols, [...bound]);
      region = isRegion(r.json);
      if (r.pieces?.length) {
        // 같은 이름의 호출이 여럿이면(조건마다 하나) 조각 번호를 이어 센다. 이름_1 이 이미 섰으면 다음은 이름_2
        const base = name || call.function.name;
        let k = 0;
        const nextName = () => { do { k += 1; } while (names.has(`${base}_${k}`)); return `${base}_${k}`; };
        // 조각이 하나뿐이면 이름 그대로 세워 뒤 호출이 그 이름을 가리킬 수 있게 한다. 둘 이상이면 이름_번호
        const single = r.pieces.length === 1 && name !== '' && !names.has(name);
        r.pieces.forEach((piece, i) => { const pieceName = single ? name : nextName(); names.add(pieceName); kindOf.set(pieceName, madeOf(entry, args, kindOf, isRegion(r.json))); out.push(withArgs(call, { ...args, [exprParam.name]: `${piece.latex}{${piece.condition}}`, name: pieceName }, `-${i + 1}`)); });
        if (name) { names.add(name); kindOf.set(name, madeOf(entry, args, kindOf, isRegion(r.json))); }
        continue;
      }
      if (r.latex === null) { rejected.push({ call, detail: `${call.function.name} 의 식 "${String(args[exprParam.name]).slice(0, 60)}": ${r.problem}` }); continue; }
      out.push(withArgs(call, { ...args, [exprParam.name]: r.latex }));
    } else {
      out.push(withArgs(call, args));
    }
    if (name) { names.add(name); const made = madeOf(entry, args, kindOf, region); if (made) kindOf.set(name, made); else kindOf.delete(name); }
    if (name && call.function.name === 'etc.slider') bound.add(name);
  }
  return { calls: out, rejected, notices };
}
